import { Flex, Menu } from "antd"
import React, { memo, useCallback, useMemo, useState } from "react"
import { useLocation } from "react-router-dom"

import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { invoicesHiddenForRole } from "../../utils/invoices-visibility.ts"
import { canAccessArchivePage } from "../Archive/archive-permissions.ts"
import { type PermissionPages } from "../SettingsPermissions/types/permissions.types.ts"
import MenuComponent from "../User/NavUserMenu.tsx"
import {
  type NavBadgeCounts,
  applyNotificationBadges
} from "./helpers/apply-notification-badges.tsx"
import { SidebarContextMenu } from "./SidebarContextMenu.tsx"
import {
  ARCHIVE_NAV_KEY,
  INVOICES_NAV_KEY,
  REPORTS_NAV_KEY,
  type SidebarItem,
  sidebarConfig
} from "./sidebar-config.tsx"
import "./styles.less"

interface SidebarProps {
  onItemSelect: (item: Pick<SidebarItem, "key">) => void
  showDefaultOpenKeys?: boolean
}

// A nav key is its route, and the permission page is that route with the
// slashes swapped.
const navKeyToPage = (key: string): PermissionPages =>
  key?.replace(/\//g, "_") as PermissionPages

const openInNewTab = (route: string) => {
  window.open(route, "_blank", "noopener,noreferrer")
}

// Ctrl on Windows/Linux, Cmd on macOS — the browser's "open link in a new
// tab" modifier.
const hasNewTabModifier = (event: React.MouseEvent | React.KeyboardEvent) =>
  event.ctrlKey || event.metaKey

const MIDDLE_BUTTON = 1

export const SidebarBody = memo(
  ({ onItemSelect, showDefaultOpenKeys }: SidebarProps) => {
    const location = useLocation()
    const permissions = useAppSelector((state) => state.auth.permissions)
    const roleId = useAppSelector((state) => state.auth.userInfo?.role?.id)
    const invoicesHidden = invoicesHiddenForRole(roleId)
    const pages = [...(permissions?.pages || []), "document_templates"]

    const getAllKeysWithParent = (): Array<{
      key: string
      parentKey?: string
    }> => {
      const items = [...sidebarConfig.main, ...sidebarConfig.bottom]

      return items.flatMap((item) =>
        "children" in item && item.children
          ? item.children.map((c) => ({
              key: String(c?.key),
              parentKey: String(item.key)
            }))
          : [{ key: String(item.key) }]
      )
    }

    const currentKeys = useMemo(() => {
      const path = location.pathname.slice(1)
      const allKeys = getAllKeysWithParent()

      const matched = allKeys
        .filter(({ key }) => path === key || path.startsWith(`${key}/`))
        .sort((a, b) => b.key.length - a.key.length)[0]

      if (!matched) return []

      return matched.parentKey
        ? [matched.parentKey, matched.key]
        : [matched.key]
    }, [location.pathname])

    const mainMenuItems: SidebarItem[] = useMemo(
      () =>
        sidebarConfig.main?.reduce((list, item) => {
          if ("children" in item && item?.children?.length) {
            const filteredItems = item.children.filter((child) =>
              pages?.includes(navKeyToPage(child?.key as string))
            )

            if (filteredItems?.length) {
              list.push({ ...item, children: filteredItems })
            }
          } else {
            // Reports is open to every authenticated user.
            if (item?.key === REPORTS_NAV_KEY) {
              list.push(item)
            } else if (item?.key === INVOICES_NAV_KEY) {
              if (pages?.includes(INVOICES_NAV_KEY) && !invoicesHidden) {
                list.push(item)
              }
            } else if (item?.key === ARCHIVE_NAV_KEY) {
              // Archive merges two former entry points — the "Inactive" leaf
              // under Leads and the Archive tab on Projects — so either
              // audience that could reach one of those still reaches this.
              if (canAccessArchivePage(permissions)) {
                list.push(item)
              }
            } else if (pages?.includes(navKeyToPage(item?.key as string))) {
              list.push(item)
            }
          }

          return list
        }, [] as SidebarItem[]),
      [pages, permissions, invoicesHidden]
    )

    const projectsCount = useAppSelector((state) => state.notificationCounts.projects)
    const calendarCount = useAppSelector((state) => state.notificationCounts.calendar)
    const approvals = useAppSelector((state) => state.notificationCounts.approvals)

    const navBadgeCounts: NavBadgeCounts = useMemo(
      () => ({
        projects: projectsCount,
        calendar: calendarCount,
        approval: approvals.total,
        proposals: approvals.proposals,
        contracts: approvals.contracts,
        document_reviews: approvals.documents
      }),
      [projectsCount, calendarCount, approvals]
    )

    const decoratedMainMenuItems = useMemo(
      () => applyNotificationBadges(mainMenuItems, navBadgeCounts),
      [mainMenuItems, navBadgeCounts]
    )

    const defaultOpenKeys = showDefaultOpenKeys ? currentKeys : []

    // Only a row that is a page maps to a route. A parent (Approval) has no
    // page of its own, so it is left out and never opens in a new tab.
    const routeByKey = useMemo(() => {
      const map = new Map<string, string>()
      const collect = (items: SidebarItem[]) => {
        items.forEach((item) => {
          if ("children" in item && item.children?.length) {
            item.children.forEach((child) => {
              const childKey = (child as { key?: React.Key })?.key
              if (childKey != null) map.set(String(childKey), `/${childKey}`)
            })
          } else {
            map.set(String(item.key), `/${item.key}`)
          }
        })
      }
      collect(mainMenuItems)
      collect(sidebarConfig.bottom)
      return map
    }, [mainMenuItems])

    const [contextMenu, setContextMenu] = useState<{
      x: number
      y: number
      route: string
    } | null>(null)

    // The route of the page row an event happened on, or null anywhere else —
    // including Approval's own title, whose data-menu-id matches no route.
    const resolveRoute = useCallback(
      (target: EventTarget): string | null => {
        const row =
          target instanceof Element
            ? target.closest<HTMLElement>("[data-menu-id]")
            : null
        if (!row) return null

        const menuId = row.getAttribute("data-menu-id") ?? ""
        const matchedKey = [...routeByKey.keys()].find(
          (key) => menuId === key || menuId.endsWith(`-${key}`)
        )
        return matchedKey ? (routeByKey.get(matchedKey) as string) : null
      },
      [routeByKey]
    )

    const handleContextMenu = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        const route = resolveRoute(event.target)
        if (!route) return

        event.preventDefault()
        setContextMenu({ x: event.clientX, y: event.clientY, route })
      },
      [resolveRoute]
    )

    // Ctrl/Cmd+click on a page row is caught on the way down, so the Menu's own
    // click — which would navigate this tab — never runs.
    const handleClickCapture = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        if (!hasNewTabModifier(event)) return
        const route = resolveRoute(event.target)
        if (!route) return

        event.preventDefault()
        event.stopPropagation()
        openInNewTab(route)
      },
      [resolveRoute]
    )

    // The Menu fires its click from Enter too, so Ctrl/Cmd+Enter gets the same
    // treatment. Key repeats are swallowed rather than let through, or holding
    // the keys would navigate this tab after the first new one opens.
    const handleKeyDownCapture = useCallback(
      (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key !== "Enter" || !hasNewTabModifier(event)) return
        const route = resolveRoute(event.target)
        if (!route) return

        event.preventDefault()
        event.stopPropagation()
        if (!event.repeat) openInNewTab(route)
      },
      [resolveRoute]
    )

    const handleAuxClick = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        if (event.button !== MIDDLE_BUTTON) return
        const route = resolveRoute(event.target)
        if (!route) return

        event.preventDefault()
        openInNewTab(route)
      },
      [resolveRoute]
    )

    // Pressing the middle button starts the browser's autoscroll on Windows;
    // over a row it means "open in a new tab" instead.
    const handleMouseDown = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        if (event.button === MIDDLE_BUTTON && resolveRoute(event.target)) {
          event.preventDefault()
        }
      },
      [resolveRoute]
    )

    const openContextMenuRoute = useCallback(() => {
      if (contextMenu) openInNewTab(contextMenu.route)
      setContextMenu(null)
    }, [contextMenu])

    return (
      <Flex
        vertical
        className={"h-full"}
        onContextMenu={handleContextMenu}
        onClickCapture={handleClickCapture}
        onKeyDownCapture={handleKeyDownCapture}
        onAuxClick={handleAuxClick}
        onMouseDown={handleMouseDown}
      >
        <SidebarContextMenu
          open={contextMenu != null}
          x={contextMenu?.x ?? 0}
          y={contextMenu?.y ?? 0}
          onOpenChange={(next) => {
            if (!next) setContextMenu(null)
          }}
          onOpenInNewTab={openContextMenuRoute}
        />
        <Flex
          vertical
          justify={"space-between"}
          flex={1}
          className={"overflow-auto"}
        >
          <Menu
            mode="inline"
            selectedKeys={currentKeys}
            defaultOpenKeys={defaultOpenKeys}
            items={decoratedMainMenuItems}
            onClick={onItemSelect}
            className="custom-sidebar-menu border-0 flex-1"
            style={{ backgroundColor: "transparent" }}
          />

          <Menu
            mode="inline"
            selectedKeys={currentKeys}
            defaultOpenKeys={defaultOpenKeys}
            items={sidebarConfig.bottom}
            onClick={onItemSelect}
            className="custom-sidebar-menu border-r-0 border-b border-border"
            style={{ backgroundColor: "transparent" }}
          />
        </Flex>
        <MenuComponent />
      </Flex>
    )
  }
)

SidebarBody.displayName = "SidebarBody"
